import { useIntl } from 'react-intl';
import PhotoCarousel from '@alltrails/cards/components/PhotoCarousel';
import TrailMetadataStats from '@alltrails/cards/components/TrailMetadataStats';
import useTrailPhotos from '@alltrails/cards/hooks/useTrailPhotos';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import trailFromAlgoliaTrail from '@alltrails/trails/utils/trailFromAlgoliaTrail';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import PhotoSize from '@alltrails/types/PhotoSize';
import styles from './styles.module.scss';

type TrailVariantCardProps = {
  trail: TrailSearchResult;
};

const TrailVariantCard = ({ trail }: TrailVariantCardProps) => {
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const { photos, loadPhotos } = useTrailPhotos(trail.ID, Boolean(trail.has_profile_photo), PhotoSize.Large, '320x320', 5);
  const trailToRender = trailFromAlgoliaTrail(trail);
  const {
    name,
    avgRating,
    defaultActivityStats: { difficulty } = {},
    trailCounts: { reviewCount },
    trailGeoStats: { length, durationMinutes },
    trailDetail
  } = trailToRender;
  const minutes = Number(durationMinutes ?? '0');
  const hasDescription = Boolean(trailDetail?.description);

  return (
    <a className={styles.trailCard} href={wrapUrlSafe(`/${trail.slug}`, languageRegionCode)} target="_blank">
      <div className={styles.photoCarousel}>
        <PhotoCarousel arrowStyle="extra-subtle" aspectRatio="square" loadPhotos={loadPhotos} photos={photos} uniqueId={trail.id} />
      </div>
      <Typography className={styles.title} component="h3" variant={isMobile ? 'heading200' : 'heading300'}>
        {name}
      </Typography>
      <TrailMetadataStats
        avgRating={avgRating}
        compact
        difficulty={difficulty}
        durationText={
          trail?.estimated_time_to_complete
            ? `${intl.formatMessage({ defaultMessage: ' Est.' })} ${trail.estimated_time_to_complete.value} ${trail.estimated_time_to_complete.unit}`
            : undefined
        }
        durationMinutes={minutes}
        lengthMeters={length}
        reviewCount={reviewCount}
        size={isMobile ? 'sm' : 'md'}
      />
      {hasDescription && (
        <Typography className={styles.description} variant="text100">
          {trailDetail?.description}
        </Typography>
      )}
    </a>
  );
};

export default TrailVariantCard;
